import { Info, Search, UserCheck } from 'lucide-react';
import * as React from 'react';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Combobox,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from '@/components/cargofl-ui';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { StatTile } from '@/components/stat-tile';
import { goalAssignmentVariant, PILL_CLASS } from '@/lib/badges';
import { useGoalOverview, type GoalSetSummary } from '@/lib/goals-api';
import { useAssignKraSet, useKraSets } from '@/lib/kra-sets-api';

/**
 * Assign KRA — on the KRA page, where the group was just written.
 *
 * It was removed once and asked for twice, and why it is safe to have back matters more
 * than why it went. The OLD tab read `GET /kra-sets/assignable-employees`: a second list
 * of the same people with its own scoping rule, while the goal screen read a different
 * one that was org-wide with no actor filter. The two disagreed about who existed, which
 * is how a manager ended up looking at all 71 employees on a screen with checkboxes.
 *
 * This one reads `useGoalOverview` — the SAME list Goal assignments renders, scoped
 * server-side to what this person may act on, with `canAssign` decided per row by the
 * same `assignBlocker` the assign itself refuses on. Two surfaces onto one list is fine;
 * two lists was the bug.
 *
 * Deliberately leaner than the goal assignments table: no publish, no export, no
 * per-employee link. You are here to point a group you have just finished at some
 * people. Making it live is the other screen's job, and the note below says so.
 */
export function KraAssignTab() {
  const { data, isPending, error } = useGoalOverview();
  const { data: sets } = useKraSets();
  const assign = useAssignKraSet();

  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [query, setQuery] = React.useState('');
  /** The group picked from the dropdown, held until it is confirmed. */
  const [pendingGroup, setPendingGroup] = React.useState<{ id: string; name: string } | null>(
    null,
  );

  const all = React.useMemo(() => data ?? [], [data]);
  const rows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) =>
      [r.employeeName, r.employeeEmail, r.designationTitle, r.departmentName].some(
        (f) => f?.toLowerCase().includes(q) ?? false,
      ),
    );
  }, [all, query]);

  const selectable = React.useMemo(() => rows.filter((r) => r.canAssign), [rows]);
  const allTicked = selectable.length > 0 && selectable.every((r) => selected.has(r.employeeId));

  /**
   * Ticked AND still visible AND still assignable. Ticking someone, then searching them
   * away, must not assign to them behind the reader's back — and the count on the button
   * is this list's length, so it never disagrees with what happens.
   */
  const toAssign = React.useMemo(
    () => selectable.filter((r) => selected.has(r.employeeId)),
    [selectable, selected],
  );

  const ready = all.filter((r) => r.canAssign).length;

  function tick(employeeId: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(employeeId);
      else next.delete(employeeId);
      return next;
    });
  }

  async function onApply(kraSetId: string, kraSetName: string) {
    try {
      const outcomes = await assign.mutateAsync({
        kraSetId,
        employeeIds: toAssign.map((r) => r.employeeId),
      });
      const assigned = outcomes.filter((o) => o.assigned);
      const skipped = outcomes.filter((o) => !o.assigned);

      // Says what it did, per person. "8 assigned, 2 skipped" and the reason beats a
      // bare success that hides the two who did not get their goals.
      if (assigned.length > 0) {
        toast.success(`"${kraSetName}" assigned to ${assigned.length} employee(s)`, {
          description:
            skipped.length > 0
              ? `${skipped.length} skipped — see below`
              : 'Review each set on Goal assignments, then publish it.',
        });
      }
      for (const skip of skipped) {
        toast.error(`Skipped ${skip.employeeName}`, { description: skip.message ?? undefined });
      }
      setSelected(new Set());
    } catch (e) {
      toast.error('Could not assign that group', {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          label="People you can assign to"
          value={isPending ? '—' : all.length}
          caption={ready === all.length ? 'none blocked' : `${ready} still need goals`}
        />
        <StatTile
          label="Already have goals"
          value={isPending ? '—' : all.length - ready}
          caption="assigning never overwrites these"
        />
        <StatTile
          label="Selected"
          value={toAssign.length}
          caption={toAssign.length === 0 ? 'tick someone to begin' : 'will get the group you pick'}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search name, email, designation or department"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <Combobox
          // Select-only: a group is a weighting that has to total 100%, so one conjured
          // from a name typed here would assign a set nobody could use.
          value={null}
          align="end"
          contentClassName="w-72"
          // Picking a group used to assign it on the spot. A dropdown item is the
          // easiest thing here to hit by accident, and the result was goals created
          // for everyone ticked — so the pick only proposes it now.
          onChange={(id) => {
            const set = (sets ?? []).find((s) => s.id === id);
            if (set) setPendingGroup({ id: set.id, name: set.name });
          }}
          options={(sets ?? []).map((set) => ({
            value: set.id,
            label: set.name,
            hint: `${set.items.length} KRAs · ${set.totalWeightage}%`,
          }))}
          searchPlaceholder="Search groups"
          emptyText="No group matches"
          trigger={
            <Button hint={"Assign this KRA group to the selected people"} className="gap-2" disabled={toAssign.length === 0 || assign.isPending}>
              <UserCheck className="size-4" />
              {assign.isPending ? 'Assigning…' : `Assign KRA group (${toAssign.length})`}
            </Button>
          }
        />
        {toAssign.length === 0 && <span className="self-center text-xs text-muted-foreground">Tick at least one person to assign</span>}
      </div>

      <ConfirmDialog
        open={pendingGroup !== null}
        onOpenChange={(next) => !next && setPendingGroup(null)}
        title={`Assign "${pendingGroup?.name ?? ''}" to ${toAssign.length} employee${toAssign.length === 1 ? '' : 's'}?`}
        description="Each of them gets that group's KRAs as their organization goals, as a draft nobody is measured on until it is published. Anyone who already has goals is skipped rather than overwritten, and you will be told who."
        confirmLabel={`Assign to ${toAssign.length}`}
        pending={assign.isPending}
        onConfirm={() => {
          const group = pendingGroup;
          setPendingGroup(null);
          if (group) void onApply(group.id, group.name);
        }}
      />

      {isPending && <Skeleton className="h-64 w-full" />}

      {!isPending && all.length === 0 && (
        <Card>
          <CardContent className="py-14 text-center">
            <UserCheck className="mx-auto mb-3 size-6 text-muted-foreground" />
            <p className="text-sm font-medium">Nobody to assign to</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
              You can assign KRAs to your own direct reports. HR can assign to anyone —
              reassigning a reporting line is theirs to do.
            </p>
          </CardContent>
        </Card>
      )}

      {!isPending && all.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="Select everyone assignable in this list"
                      checked={allTicked}
                      disabled={selectable.length === 0}
                      onCheckedChange={(on) =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          for (const row of selectable) {
                            if (on) next.add(row.employeeId);
                            else next.delete(row.employeeId);
                          }
                          return next;
                        })
                      }
                    />
                  </TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Goal set</TableHead>
                  <TableHead>From group</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <EmployeeRow
                    key={row.employeeId}
                    row={row}
                    ticked={selected.has(row.employeeId)}
                    onToggle={(on) => tick(row.employeeId, on)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          <p className="text-xs text-muted-foreground">
            Showing {rows.length} of {all.length}. Assigning creates a DRAFT set — review it
            on Goal assignments, then publish it there.
          </p>
        </>
      )}
    </div>
  );
}

function EmployeeRow({
  row,
  ticked,
  onToggle,
}: {
  row: GoalSetSummary;
  ticked: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <TableRow className={ticked ? 'bg-accent/50' : undefined}>
      <TableCell>
        {row.canAssign ? (
          <Checkbox
            aria-label={`Select ${row.employeeName}`}
            checked={ticked}
            onCheckedChange={(on) => onToggle(on === true)}
          />
        ) : (
          // Blocked rows cannot be ticked at all, rather than being ticked and then
          // silently skipped on Apply. This is the guarantee the screen rests on: a
          // bulk action must never quietly overwrite goals somebody already wrote.
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex size-4 items-center justify-center">
                <Info className="size-3.5 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-64">{row.blockedMessage}</TooltipContent>
          </Tooltip>
        )}
      </TableCell>

      <TableCell>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.employeeName}</p>
          <p className="truncate text-xs text-muted-foreground">{row.employeeEmail}</p>
        </div>
      </TableCell>

      <TableCell className="text-sm">
        {row.departmentName ?? <span className="text-muted-foreground">—</span>}
        {row.parentDepartmentName && (
          <span className="block text-xs text-muted-foreground">
            under {row.parentDepartmentName}
          </span>
        )}
      </TableCell>

      <TableCell className="text-sm">
        {row.designationTitle ?? <span className="text-muted-foreground">—</span>}
      </TableCell>

      <TableCell>
        <Badge variant={goalAssignmentVariant(row.status)} className={PILL_CLASS}>
          {row.status === 'NOT_STARTED'
            ? 'Not started'
            : row.status.replace(/_/g, ' ').toLowerCase()}
        </Badge>
        {row.orgGoalCount > 0 && (
          <span className="mt-1 block text-xs text-muted-foreground">
            {row.orgGoalCount} goals · {row.orgTotalWeightage}%
          </span>
        )}
      </TableCell>

      <TableCell className="text-sm">
        {row.assignedFromName ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
    </TableRow>
  );
}
